import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { safePersistStorage } from '../../../shared/utils/safePersistStorage';
import { createDataProcessorSlice } from './slices/dataProcessorSlice';
import { createWeightingMethodSlice } from './slices/weightingMethodSlice';
import { createRebalanceSchedulerSlice } from './slices/rebalanceSchedulerSlice';
import { createSecurityScoringSlice } from './slices/securityScoringSlice';
import { createUtilitySlice } from './slices/utilitySlice';
import type { DefinitionFormStoreState } from './types/definitionFormStoreTypes';
import {
  DEFAULT_UNIVERSE_ID,
  resolveUniverseId,
} from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/StartingUniverse/config/constants/staticUniverses';

export const useSmartBetaStrategyStore = create<DefinitionFormStoreState>()(
  devtools(
    persist(
      immer((...a) => ({
        ...createDataProcessorSlice(...a),
        ...createRebalanceSchedulerSlice(...a),
        ...createSecurityScoringSlice(...a),
        ...createWeightingMethodSlice(...a),
        ...createUtilitySlice(...a),
      })),
      {
        name: 'smart-beta-strategy-storage',
        storage: safePersistStorage('session'),
        // v2: the scheduler moved to the backend shape
        // (reconstitution/rebalance → recon_scheduler/is_rebal_activated/
        // rebal_scheduler). Stale persisted state from v1 lacks
        // recon_scheduler and would crash consumers reading
        // `.recon_scheduler.frequency`, so migrate drops the obsolete
        // scheduler slice and lets the fresh defaults seed it.
        // v3: the universe is keyed by portfolio_id instead of portfolio_name.
        version: 3,
        migrate: (persisted: unknown, version: number) => {
          if (!persisted || typeof persisted !== 'object') return persisted;
          let next = persisted as Record<string, unknown>;

          if (version < 2) {
            const {
              rebalance_scheduler: _staleScheduler,
              schedulerSeededUniverse: _staleSeed,
              ...rest
            } = next;
            void _staleScheduler;
            void _staleSeed;
            next = rest;
          }

          if (version < 3) {
            // v2 keyed the universe by DISPLAY NAME, in whichever of three
            // vocabularies happened to be live when it was persisted. Map it
            // to the id; an unrecognised name falls back to the default index
            // rather than silently resolving to the first catalogue entry.
            const dp = next.data_processor as
              { universe?: Record<string, unknown> } | undefined;
            const universe = dp?.universe;
            if (universe && typeof universe.portfolio_name === 'string') {
              const { portfolio_name: legacyName, ...restUniverse } = universe;
              dp.universe = {
                ...restUniverse,
                portfolio_id:
                  resolveUniverseId(legacyName) ??
                  DEFAULT_UNIVERSE_ID,
              };
            }
            // The seeded-universe marker is an id now too; drop the stale name
            // so useUniverseSync re-seeds once instead of comparing across
            // vocabularies and never matching.
            const { schedulerSeededUniverse: _staleSeed, ...rest } = next;
            void _staleSeed;
            next = rest;
          }

          return next;
        },
        // Exclude transient UI state from persistence so the preview
        // drawer always defaults to closed on page load. Persisting
        // it caused the drawer to spring open on every refresh once
        // a user had ever opened it during the session.
        partialize: (state) => {
          const { isPreviewOpen: _omit, ...rest } =
            state as DefinitionFormStoreState & {
              isPreviewOpen?: boolean;
            };
          void _omit;
          return rest;
        },
      },
    ),
    { name: 'SmartBetaStrategyForm' },
  ),
);

// ============================================================================
// SELECTORS (for optimized re-renders)
// ============================================================================

export const selectDataProcessor = (state: DefinitionFormStoreState) =>
  state.data_processor;
export const selectRebalanceScheduler = (state: DefinitionFormStoreState) =>
  state.rebalance_scheduler;
export const selectSecurityScoringSteps = (state: DefinitionFormStoreState) =>
  state.steps;
